/* eslint-disable jsx-a11y/control-has-associated-label,import/order,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call */
import cn from 'classnames';
import he from 'he';
import { useRef, useState, useEffect, useCallback } from 'react';
import ReactPlayer from 'react-player/youtube';
import 'intersection-observer';
import { useIsVisible } from 'react-is-visible';

import { useGetAiPlayerListQuery, useRunRecognizeVideoMutation } from '@shared/api';
import type { Locale } from '@shared/libs';
import { parseMusicians } from '@shared/libs';
import type { NotesCardQueryData } from '@shared/types';
import { unbounded, H2, Rating } from '@shared/ui';

import type { TVideoItem } from '../model';
import { Active } from './active';
import styles from './ai-player.module.css';
import { Description } from './description';
import { Ready } from './labels';
import { Variants } from './variants';
import { ENV } from '@/shared/config';

type TProps = {
  locale: Locale;
  data: NotesCardQueryData['data'];
};

export const AiPlayer = ({
  locale,
  data: { title, rating, vote_count, musicians, mp3, id },
}: TProps) => {
  const { data, isSuccess } = useGetAiPlayerListQuery({
    id,
    title,
    musician: musicians?.[0]?.title || '',
  });

  const nodeRef = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(nodeRef);
  const refPlayer = useRef<ReactPlayer>(null);
  const firstRender = useRef(true);
  const [isPlay, setIsPlay] = useState(false);
  const [activeItem, setActiveItem] = useState<TVideoItem | null>(null);
  const [startMs, setStartMs] = useState<number | null>(null);

  const onChange = useCallback(
    (val: number) => {
      refPlayer.current?.seekTo(val, 'seconds');

      if (!isPlay && firstRender.current) {
        firstRender.current = false;
        setIsPlay(true);
      }
    },
    [isPlay],
  );

  const firstPlay = useRef(true);
  useEffect(() => {
    if (isPlay && firstPlay.current) {
      firstPlay.current = false;
      if (process.env.NODE_ENV !== 'development') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        window.ym?.(Number(process.env.NEXT_PUBLIC_YA_METRIKA), 'reachGoal', 'AI_PLAYER_PLAY');
      }
    }
  }, [isPlay]);

  const firstVisible = useRef(false);

  const firstRenderRec = useRef(true);

  const mutation = useRunRecognizeVideoMutation();

  useEffect(() => {
    if (isVisible && !firstVisible.current && activeItem) {
      firstVisible.current = true;
      if (process.env.NODE_ENV !== 'development') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        window.ym?.(
          Number(process.env.NEXT_PUBLIC_YA_METRIKA),
          'reachGoal',
          activeItem.ready ? 'AI_PLAYER_SHOW_DATA' : 'AI_PLAYER_SHOW_EMPTY',
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, activeItem]);

  useEffect(() => {
    firstVisible.current = false;
    if (data?.data?.length) {
      setActiveItem(data?.data[0]);
    }
    if (data?.data?.length === 0) {
      if (process.env.NODE_ENV !== 'development') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        window.ym?.(Number(process.env.NEXT_PUBLIC_YA_METRIKA), 'reachGoal', 'AI_PLAYER_NO_VIDEO');
      }
    }
  }, [data]);

  useEffect(() => {
    firstRender.current = true;
    firstPlay.current = true;
    setIsPlay(false);
    setStartMs(null);
  }, [activeItem]);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_DISABLED_AUTO_RECOGNIZE === 'true') {
      return;
    }

    if (firstRenderRec.current && activeItem) {
      if (!activeItem.ready && !/\bHeadlessChrome\//.test(navigator.userAgent)) {
        mutation.mutate({ id: activeItem.id });
      }

      firstRenderRec.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem]);

  return (
    <div ref={nodeRef}>
      {!isSuccess || !data?.success || !data?.data?.length ? null : (
        <div id="ai-player" ref={nodeRef} className={styles.wrap}>
          <H2 className={styles.titleBlock}>{locale.AI_PLAYER_TITLE || 'AI_PLAYER_TITLE'}</H2>

          {ENV.LANG === 'ru' && (
            <div className={styles.container}>
              <div className={styles.accordesBlock}>
                <p className={cn(styles.ruBlock, unbounded.className)}>
                  {locale.AI_PLAYER_RU_MSG || 'AI_PLAYER_RU_MSG'}
                </p>
              </div>
            </div>
          )}
          <div className={styles.container}>
            <div className={styles.accordesBlock}>
              <div className={styles.card}>
                <div className={styles.header}>
                  <div className={styles.headerLeft}>
                    {activeItem?.ready && (
                      <div className={styles.labels}>
                        <Ready locale={locale} />
                      </div>
                    )}
                    <p className={cn(unbounded.className, styles.title)}>
                      {parseMusicians(musicians)} - {he.decode(title || '')}
                    </p>
                    {activeItem?.title && <p className={styles.otherTitle}>{activeItem?.title}</p>}
                  </div>
                  <div className={styles.headerRight}>
                    {false && (
                      <Rating id={0} rate={rating} locale={locale} vote_count={vote_count} large />
                    )}
                  </div>
                </div>
                {activeItem && (
                  <Active
                    locale={locale}
                    isPlay={isPlay}
                    setIsPlay={setIsPlay}
                    mp3={mp3}
                    startMs={startMs}
                    onChange={onChange}
                    idNote={id}
                    isVisible={isVisible}
                    refPlayer={refPlayer}
                    {...activeItem}
                  />
                )}
              </div>
            </div>
            <div className={styles.videoBlock}>
              <div className={styles.videoWrap}>
                <ReactPlayer
                  ref={refPlayer}
                  url={`https://www.youtube.com/watch?v=${activeItem?.id}`}
                  // light
                  playing={isPlay}
                  pip={false}
                  controls
                  height={268}
                  width="100%"
                  onProgress={() => {
                    const time = refPlayer.current?.getCurrentTime();
                    setStartMs(time === undefined ? null : time);
                  }}
                  onPlay={() => {
                    setIsPlay(true);
                    const time = refPlayer.current?.getCurrentTime();
                    setStartMs(time === undefined ? null : time);
                  }}
                  onEnded={() => {
                    setIsPlay(false);
                    setStartMs(null);
                  }}
                  onPause={() => {
                    setIsPlay(false);
                  }}
                  style={{
                    borderRadius: 44,
                    overflow: 'hidden',
                    backgroundColor: 'black',
                  }}
                />
              </div>

              {activeItem && <Description locale={locale} activeItem={activeItem} />}

              <div className={styles.videoInfo}>
                {data?.data?.length && (
                  <Variants
                    locale={locale}
                    variants={data?.data}
                    setActiveItem={setActiveItem}
                    activeItem={activeItem}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
